import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomerDropdown, getDetailData } from "../model/DataProcessingModel.jsx";
 
const DataDetailVM = () => {
  // Get query parameters from URL
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const batchId = queryParams.get("batchId");
  const customerName = queryParams.get("customer");
  const projectName = queryParams.get("project");
  // api name changed
  const [standards, setStandards] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedDetailData, setSelectedDetailData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
 
  // New state moved from EditMaterialDialog
  const [attributeValues, setAttributeValues] = useState({});
  const [localEditData, setLocalEditData] = useState({});
  const [editDialogData, setEditDialogData] = useState(null);
  const [changesTracking, setChangesTracking] = useState({});
  const [originalData, setOriginalData] = useState({});
  const tool = queryParams.get("tool") || "Enrichment";
 
  // Navigation function
  const handleBack = () => {
    navigate("/data-operation-centre/history?tool=Enrichment");
  };
 
  // Fetch customer dropdown data (standards and commodities)
  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        console.log("Fetching customer data for tool:", tool);
 
        // Pass the tool parameter to getCustomerDropdown
        const customerData = await getCustomerDropdown(tool);
 
        console.log("customerData", customerData);
 
        setStandards(customerData.standards || []);
        setCommodities(customerData.commodities || []);
      } catch (error) {
        console.error("Error fetching customer data:", error);
        setStandards([]);
        setCommodities([]);
      }
    };
 
    // Only fetch if tool is available
    if (tool) {
      fetchCustomerData();
    }
  }, [tool]); // Add tool as dependency
 
  // Fetch detail data when batchId changes
  useEffect(() => {
    const fetchDetailData = async () => {
      if (batchId) {
        setIsLoading(true);
        try {
          const response = await getDetailData(batchId);
          setSelectedDetailData(response);
        } catch (error) {
          setSelectedDetailData([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
 
    fetchDetailData();
  }, [batchId]);
 
  // Initialize form data when editData changes (from modal)
  const initializeFormData = (editData) => {
    if (editData) {
      setLocalEditData({ ...editData });
      setEditDialogData(editData);
      setOriginalData({ ...editData }); // Save original data for tracking changes
      setChangesTracking({}); // Reset changes tracking
 
      // Initialize attribute values
      if (editData.attribute && typeof editData.attribute === 'object') {
        setAttributeValues({ ...editData.attribute });
      }
    }
  };
 
  // Handle local field changes and track changes
  const handleLocalFieldChange = (fieldName, value) => {
    // Check if this is a real change from the original data
    const originalValue = originalData[fieldName];
    if (value !== originalValue) {
      // Update changes tracking
      setChangesTracking(prev => ({
        ...prev,
        [fieldName]: {
          oldValue: originalValue,
          newValue: value
        }
      }));
    } else {
      // Remove from changes tracking if it's back to original
      setChangesTracking(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
 
    // Update the local state
    setLocalEditData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
 
  // Handle attribute field changes
  const handleAttributeChange = (fieldName, value) => {
    // Track attribute changes
    const originalValue = originalData.attribute?.[fieldName];
    if (value !== originalValue) {
      setChangesTracking(prev => ({
        ...prev,
        [`attribute.${fieldName}`]: {
          oldValue: originalValue,
          newValue: value
        }
      }));
    } else {
      setChangesTracking(prev => {
        const updated = { ...prev };
        delete updated[`attribute.${fieldName}`];
        return updated;
      });
    }
 
    // Update local attribute state
    const updatedAttributeValues = {
      ...attributeValues,
      [fieldName]: value
    };
 
    setAttributeValues(updatedAttributeValues);
 
    // Update local editData state
    const currentAttribute = localEditData.attribute || {};
 
    // Create a new attribute object with updated value
    const updatedAttribute = {
      ...currentAttribute,
      [fieldName]: value
    };
 
    // Update local editData state
    setLocalEditData(prev => ({
      ...prev,
      attribute: updatedAttribute
    }));
  };
 
  // Helper function to safely get value from editData with fallbacks
  const getValue = (fieldName, defaultValue = '') => {
    // Check local state first
    if (localEditData && localEditData[fieldName] !== undefined) {
      return localEditData[fieldName];
    }
 
    // Then check original editData with different field name variations
    if (editDialogData) {
      const value =
        editDialogData[fieldName] !== undefined ? editDialogData[fieldName] :
          editDialogData[fieldName.replace(/\s+/g, '_')] !== undefined ? editDialogData[fieldName.replace(/\s+/g, '_')] :
            editDialogData[fieldName.toLowerCase()] !== undefined ? editDialogData[fieldName.toLowerCase()] :
              editDialogData[fieldName.toLowerCase().replace(/\s+/g, '_')] !== undefined ? editDialogData[fieldName.toLowerCase().replace(/\s+/g, '_')] :
                defaultValue;
 
      return value;
    }
 
    return defaultValue;
  };
 
  // Monitor attributes and standard names for automatic status updates
  useEffect(() => {
    // Only apply the "Not Enriched" logic if the current status isn't already "Processed"
    if (localEditData?.status !== 'Processed') {
      const attributeFieldNames = getAttributeFieldNames();
 
      const areAttributesEmpty = !attributeFieldNames ||
        attributeFieldNames.length === 0 ||
        attributeFieldNames.every(field => !attributeValues[field]);
 
      const areStandardNamesEmpty = !localEditData?.standard_name && !localEditData?.standard_name_2;
 
      if (areAttributesEmpty && areStandardNamesEmpty) {
        handleLocalFieldChange('status', 'Not Enriched');
      }
    }
  }, [attributeValues, localEditData?.standard_name, localEditData?.standard_name_2, localEditData?.status]);
 
  // Get attribute field names for dynamic rendering
  const getAttributeFieldNames = () => {
    return attributeValues ? Object.keys(attributeValues) : [];
  };
 
  // Handle setting comment with automatic status update
  const handleCommentChange = (value) => {
    // Update comments first
    handleLocalFieldChange('comments', value);
 
    // If comments is set to "Enriched", automatically set status to "Processed"
    if (value === 'Enriched') {
      setTimeout(() => {
        handleLocalFieldChange('status', 'Processed');
      }, 0);
    }
  };
 
  // Get all tracked changes
  const getAllChanges = () => {
    return changesTracking;
  };
 
  // Log all changes on save
  const handleSaveChanges = () => {
    // Return the complete updated data
    return localEditData;
  };
 
  // Search functions
  const openMaterialSearch = (text) => {
    const query = encodeURIComponent(text || '');
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
  };
 
  const openManufacturerSearch = (manufacturer, partNumber) => {
    const name = manufacturer?.trim() || '';
    const part = partNumber?.trim() || '';
    const query = encodeURIComponent(`${name} ${part}`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
  };
 
  return {
    // Original returns
    batchId,
    customerName,
    projectName,
    standards,
    commodities,
    selectedDetailData,
    isLoading,
    handleBack,
 
    // New returns for EditMaterialDialog
    attributeValues,
    localEditData,
    initializeFormData,
    handleLocalFieldChange,
    handleAttributeChange,
    handleCommentChange,
    getValue,
    getAttributeFieldNames,
    openMaterialSearch,
    openManufacturerSearch,
 
    // Change tracking and saving
    getAllChanges,
    handleSaveChanges,
    setSelectedDetailData
  };
};
 
export default DataDetailVM;
